import { useCallback, useEffect, useState } from "react";
import { Check, X, Loader2, MapPin, LayoutGrid, Clock, FolderPlus, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PETROLE = "#284E55";
const TABS = ["Prospects", "Clients", "Projets", "Chantiers", "Artisans", "Documents", "Factures", "Mes territoires", "Mon abonnement"];
const EMPTY_TABS = ["Clients", "Projets", "Chantiers", "Artisans", "Documents", "Factures"];

const ROUTING_LABELS = {
  WAITING_PARTNER: "Nouveau projet", PROPOSED_TO_NEIGHBOR: "Projet proposé (secteur voisin)",
  ACCEPTED: "Accepté", NEIGHBOR_ACCEPTED: "Accepté", PARTNER_REFUSED: "Refusé", CENTRAL_REVIEW: "Transféré à la Centrale",
};

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl border-2 border-[#EDEAE3] bg-white p-4">
    <p className="font-display text-2xl font-medium text-navy">{value}</p>
    <p className="text-xs font-bold uppercase tracking-wider text-stone-400">{label}</p>
  </div>
);

export const PartnerDashboardSection = ({ token }) => {
  const [tab, setTab] = useState("Prospects");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const [createdProjects, setCreatedProjects] = useState({});

  const fetchDashboard = useCallback(() => {
    api.get(`/partner-portal/${token}/dashboard`).then((res) => {
      setData(res.data);
      res.data.prospects.forEach((p) => {
        if (p.phase3_project_id) {
          api.post(`/partner-portal/${token}/prospects/${p.id}/create-client-project`)
            .then((r) => setCreatedProjects((prev) => ({ ...prev, [p.id]: r.data.project })))
            .catch(() => {});
        }
      });
    }).catch(() => {});
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const createClientProject = async (prospectId) => {
    setCreatingId(prospectId);
    try {
      const res = await api.post(`/partner-portal/${token}/prospects/${prospectId}/create-client-project`);
      setCreatedProjects((prev) => ({ ...prev, [prospectId]: res.data.project }));
      toast.success(res.data.already_exists ? "Projet client déjà existant" : "Projet client créé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingId(null);
    }
  };

  const act = async (prospectId, action) => {
    setBusy(prospectId);
    try {
      await api.post(`/partner-portal/${token}/prospects/${prospectId}/${action}`);
      toast.success(action === "accept" ? "Projet accepté" : "Projet refusé");
      fetchDashboard();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return <div className="mt-8 flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" style={{ color: PETROLE }} /></div>;
  }

  const actionable = data.prospects.filter((p) => ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routing_status));
  const others = data.prospects.filter((p) => !["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routing_status));

  return (
    <div data-testid="partner-dashboard-section" className="mt-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Tableau de bord</p>

      {data.territory_pending_activation && (
        <div data-testid="partner-territory-pending-banner" className="mt-3 flex items-start gap-3 rounded-2xl p-4 text-sm leading-relaxed" style={{ backgroundColor: `${PETROLE}0D`, color: PETROLE }}>
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          Votre territoire est en attente d'activation par CozyHome. Votre espace est déjà accessible, mais aucun
          projet ne vous sera transmis avant l'activation.
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Nouveaux" value={data.stats.nouveaux} />
        <StatCard label="Acceptés" value={data.stats.acceptes} />
        <StatCard label="Refusés" value={data.stats.refuses} />
        <StatCard label="Total" value={data.stats.total} />
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`partner-dash-tab-${t}`}
            onClick={() => setTab(t)}
            className="shrink-0 rounded-full border-2 px-4 py-2 text-xs font-bold transition-colors duration-200"
            style={tab === t ? { backgroundColor: PETROLE, borderColor: PETROLE, color: "#fff" } : { borderColor: "#EDEAE3", color: "#0A1128" }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Prospects" && (
        <div data-testid="partner-prospects-list" className="mt-5 space-y-3">
          {actionable.length === 0 && others.length === 0 && (
            <p className="py-10 text-center text-sm text-stone-400">Aucun projet pour le moment.</p>
          )}
          {actionable.map((p) => (
            <div key={p.id} data-testid={`prospect-row-${p.id}`} className="rounded-2xl border-2 border-[#EDEAE3] bg-white p-5">
              <p className="text-xs font-bold uppercase" style={{ color: PETROLE }}>{ROUTING_LABELS[p.routing_status]}</p>
              <p className="mt-1 text-sm font-bold text-navy">{p.city} — {p.project_type || "Projet"}</p>
              <p className="mt-1 text-xs text-stone-500">{p.budget || "Budget non précisé"} · {p.firstname} {p.lastname}</p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid={`prospect-accept-${p.id}`}
                  disabled={busy === p.id}
                  onClick={() => act(p.id, "accept")}
                  className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: PETROLE }}
                >
                  <Check className="h-3.5 w-3.5" /> Accepter
                </button>
                <button
                  data-testid={`prospect-refuse-${p.id}`}
                  disabled={busy === p.id}
                  onClick={() => act(p.id, "refuse")}
                  className="flex items-center gap-1.5 rounded-full border-2 border-[#EDEAE3] px-4 py-2 text-xs font-bold text-navy transition-colors duration-300 hover:border-red-300 disabled:opacity-50"
                >
                  <X className="h-3.5 w-3.5" /> Refuser
                </button>
              </div>
            </div>
          ))}
          {others.map((p) => (
            <div key={p.id} data-testid={`prospect-row-${p.id}`} className="rounded-2xl border border-[#EDEAE3] bg-white/60 p-5 opacity-70">
              <p className="text-xs font-bold uppercase text-stone-400">{ROUTING_LABELS[p.routing_status] || p.routing_status}</p>
              <p className="mt-1 text-sm font-bold text-navy">{p.city} — {p.project_type || "Projet"}</p>
              {["ACCEPTED", "NEIGHBOR_ACCEPTED"].includes(p.routing_status) && (
                <div className="mt-3">
                  {createdProjects[p.id] ? (
                    <div data-testid={`client-project-created-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#EDEAE3]/50 p-3">
                      <span className="flex items-center gap-1.5 text-xs font-bold text-navy">
                        <CheckCircle2 className="h-3.5 w-3.5" style={{ color: PETROLE }} /> Projet client créé · {createdProjects[p.id].reference}
                      </span>
                      <button
                        data-testid={`copy-client-link-${p.id}`}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/espace-client/${createdProjects[p.id].reference}`);
                          toast.success("Lien copié");
                        }}
                        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white transition-opacity duration-300 hover:opacity-90"
                        style={{ backgroundColor: PETROLE }}
                      >
                        <Copy className="h-3 w-3" /> Copier le lien
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid={`create-client-project-${p.id}`}
                      disabled={creatingId === p.id}
                      onClick={() => createClientProject(p.id)}
                      className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: PETROLE }}
                    >
                      {creatingId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
                      Créer le projet client
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "Mes territoires" && (
        <div data-testid="partner-my-territories" className="mt-5 space-y-3">
          {data.territories.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white p-5">
              <MapPin className="h-4 w-4 shrink-0" style={{ color: PETROLE }} />
              <div>
                <p className="text-sm font-bold text-navy">{t.name}</p>
                <p className="text-xs text-stone-500">{t.department} · {t.commune_count} communes</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Mon abonnement" && (
        <div data-testid="partner-my-subscription" className="mt-5 rounded-2xl p-6 text-white" style={{ backgroundColor: PETROLE }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Abonnement CozyHome</p>
          <p className="mt-2 font-display text-3xl font-medium">
            {data.partner.monthly_price_ht} € <span className="text-base text-white/70">HT / mois</span>
          </p>
          <p className="mt-2 text-sm text-white/80">
            {data.territories.length} territoire{data.territories.length > 1 ? "s" : ""} actif{data.territories.length > 1 ? "s" : ""}
          </p>
        </div>
      )}

      {EMPTY_TABS.includes(tab) && (
        <div data-testid={`partner-dash-empty-${tab}`} className="mt-5 flex flex-col items-center rounded-2xl border-2 border-dashed border-[#EDEAE3] py-14 text-center">
          <LayoutGrid className="h-6 w-6 text-stone-300" />
          <p className="mt-3 text-sm text-stone-400">Aucune donnée pour le moment.</p>
        </div>
      )}
    </div>
  );
};
